if(!customElements.get('cart-drawer')) {
  /**
   * Cart Drawer Web Component
   *
   * A custom element that manages the shopping cart drawer functionality,
   * including opening/closing, refreshing cart contents, and handling user interactions.
   *
   * @class CartDrawer
   * @extends HTMLElement
   */
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      /** @type {HTMLElement|null} Element that had focus before drawer opened */
      this.focusedElementBeforeOpen = null;
    }

    open() {
      this.focusedElementBeforeOpen = document.activeElement;
      scrollLock.enabledBodyScrollLock();
      // Request animation frame for smoother animation process
      requestAnimationFrame(() => {
        this.setAttribute('open', '');
        // If quick view is open, close it
        if(document.querySelector("quick-add-modal[open]")) {
          document.querySelector("quick-add-modal[open]").removeAttribute("open")
        }
        // Trap element focus within the drawer
        trapFocus(this);
        const firstFocusableElement = getFocusableElements(this)[0];
        if (firstFocusableElement) {
          firstFocusableElement.focus();
        }
        this.dispatchEvent(new CustomEvent('cart-drawer:open', { bubbles: true }));
      });
    }

    /**
     * Closes the cart drawer and restores focus
     *
     * - Disables body scroll lock
     * - Removes styling and open attributes
     * - Waits for animation to complete before restoring focus
     * - Dispatches 'cart-drawer:closed' event
     */
    close() {
      scrollLock.disableBodyScrollLock();
      this.removeAttribute('style');
      this.removeAttribute('open');

      const handleAnimationEnd = () => {
        removeTrapFocus();
        if (this.focusedElementBeforeOpen) {
          this.focusedElementBeforeOpen.focus();
        }
        this.removeEventListener('animationend', handleAnimationEnd);
      };

      this.addEventListener('animationend', handleAnimationEnd);
      this.dispatchEvent(new CustomEvent('cart-drawer:closed', { bubbles: true }));
    }

    /**
     * Handles click events for drawer close actions
     * @param {Event} event - The click event
     * @param {boolean} shouldPreventDefault - Whether to prevent the default event behavior
     */
    handleClick(event, shouldPreventDefault) {
      if(shouldPreventDefault) event.preventDefault();
      event.stopPropagation();
      this.close();
    }

    /**
     * Initializes event listeners for close and continue buttons
     *
     * Sets up click handlers for:
     * - Close buttons (.js-cart-close)
     * - Continue shopping button (.js-continue-button)
     */
    initButtons() {
      this.closeButtons = document.querySelectorAll('.js-cart-close');
      this.continueButton = this.querySelector('.js-continue-button');

      if(this.closeButtons) this.closeButtons.forEach((button) => button.addEventListener('click', (event) => this.handleClick(event, true)));
      if(this.continueButton) this.continueButton.addEventListener('click', (event) => this.handleClick(event, true));
    }

    /**
     * Updates button states based on cart contents
     *
     * - Shows checkout button when cart has items
     * - Shows continue shopping button when cart is empty
     * - Reinitializes button event listeners
     *
     * @param {number} itemsInCart - Number of items currently in the cart
     */
    updateButtons(itemsInCart) {
      const cartHeadButton = this.querySelector('.js-cart-head-button');
      const continueHolder = this.querySelector('.js-continue-holder');

      if(itemsInCart > 0) {
        this.checkoutButton = this.checkoutButtonTemplate.content.firstElementChild.cloneNode(true);
        if(this.checkoutButton) cartHeadButton.innerHTML = this.checkoutButton.outerHTML;
        if(continueHolder){
          this.continueButton = this.continueButtonTemplate.content.firstElementChild.cloneNode(true);
          if(this.continueButton) continueHolder.innerHTML = this.continueButton.outerHTML;
        }
      } else {
        this.continueButton = this.continueButtonTemplate.content.firstElementChild.cloneNode(true);
        if(this.continueButton){
          cartHeadButton.innerHTML = this.continueButton.outerHTML;
          if(continueHolder) continueHolder.innerHTML = "";
        }
      }

      this.initButtons();
    }

    /**
     * Processes URL hash to auto-open cart drawer
     *
     * - Checks if URL contains '#cart'
     * - Opens cart drawer if not already open
     * - Uses header cart trigger to maintain proper event flow
     */
    processUrl() {
      const href = window.location.href.split('#')[1];
      // If we are not at #cart, disregard and move on
      if(!href?.includes('cart')) return;

      const headerTrigger = document.querySelector('.header__icons .js-cart-trigger');
      // If we cannot find the cart trigger in the header, move on
      if(!headerTrigger) return;

      // If cart drawer isn't already open, trigger it
      if(!this.getAttribute('open')){
        headerTrigger.click();
      }
    }

    /**
     * Sets up event listeners for cart drawer open triggers
     *
     * Adds click handlers to all elements with .js-cart-trigger class
     * Each trigger will open the cart drawer when clicked
     */
    setupTriggers() {
      if(!this.openTriggers) return;

      this.openTriggers.forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.open();
        });
      });
    }

    /**
     * Caches references to important DOM elements
     *
     * Sets up references to frequently accessed elements:
     * - Cart trigger buttons
     * - Button templates
     * - Inner drawer container
     * - Cart-specific components (announcements, notes, GWP selector)
     */
    setElements(){
      this.openTriggers = document.querySelectorAll('.js-cart-trigger');
      this.section = this.closest(`section.shopify-section`);
      this.continueButtonTemplate = this.querySelector('#continue-button-template');
      this.checkoutButtonTemplate = this.querySelector('#checkout-button-template');
      this.inner = this.querySelector('.js-drawer-inner');
      this.cartAnnouncement = this.querySelector('cart-announcement');
      this.cartNote = this.querySelector('cart-note');
      this.cartGWPSelector = this.querySelector('cart-gwp-selector');
    }

    /**
     * Initializes the cart drawer component
     *
     * Runs all setup methods in the correct order:
     * 1. Cache DOM element references
     * 2. Set up button event listeners
     * 3. Set up trigger event listeners
     * 4. Update header cart count
     * 5. Process URL for auto-opening
     * 6. Set up cart event listeners
     *
     * Also sets global reference for backward compatibility
     */
    initialize() {
      this.setElements();
      this.initButtons();
      this.setupTriggers();
      this.setHeaderCount();
      this.processUrl();
      this.setupCartEventListeners();

      window.cartDrawer = this;
    }

    /**
     * Updates the cart count display in the header
     *
     * - Reads current item count from drawer data
     * - Updates header cart count text
     * - Shows/hides cart count bubble based on item count
     */
    setHeaderCount(){
      const currentItems = parseInt(this.querySelector('.js-items-count').getAttribute('data-js-current-items'), 10);
      const headerBlob = document.querySelector('.js-header-cart-count');
      const bubble = document.querySelector('.cart-count-bubble');

      if(headerBlob) headerBlob.innerHTML = currentItems;

      if (bubble && +currentItems > 0) {
        bubble.classList.remove('hidden');
      } else if(bubble) {
        bubble.classList.add('hidden');
      }
    }

        /**
     * Sets up event listeners for cart-related events
     *
     * Listens for 'cart:item:added' events and:
     * - Always refreshes the cart drawer content
     * - Opens the drawer if event.detail.open is true
     *
     * @listens cart:item:added
     */
    setupCartEventListeners() {
      // Listen for cart items being added
      document.addEventListener('cart:item:added', (event) => {
        // Always refresh the cart drawer when items are added
        this.refresh();

        // Only open the drawer if the event specifies to do so
        if (event.detail && event.detail.open === true) {
          this.open();
        }
      });
    }

    /**
     * Refreshes the cart drawer content from the server
     *
     * - Fetches fresh cart data from Shopify sections API
     * - Supports A/B testing with redesign layout parameter
     * - Replaces current drawer content with updated content
     * - Reinitializes component after content replacement
     * - Dispatches refresh events for other components to listen to
     *
     * @fires cart-drawer:refresh - Before content is replaced
     * @fires cart-drawer:refreshed - After content is replaced and reinitialized
     */
    refresh() {
      let section = 'cart-drawer';
      let fetchParam = section;

      fetch(`/?sections=${fetchParam}`)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.handleErrorMessage(response.description);
            return;
          }

          const content = response[section];
          const html = new DOMParser().parseFromString(content, 'text/html');
          const cartDrawerSection = html.querySelector(`#shopify-section-${section}`);

          if (cartDrawerSection) {
            const newDrawer = cartDrawerSection.querySelector('cart-drawer');
            if (newDrawer) {
              const fragment = document.createDocumentFragment();

              while (newDrawer.firstChild) {
                fragment.appendChild(newDrawer.firstChild);
              }

              while (this.firstChild) {
                this.removeChild(this.firstChild);
              }

              this.appendChild(fragment);

              this.dispatchEvent(new CustomEvent('cart-drawer:refresh', { bubbles: true }));

              this.initialize();

              this.dispatchEvent(new CustomEvent('cart-drawer:refreshed', {
                bubbles: true,
                detail: {
                  firstItem: this.querySelector('cart-item:first-of-type')
                }
              }));
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    }

    /**
     * Web Components lifecycle method - called when element is added to DOM
     *
     * Automatically initializes the cart drawer when it's connected to the DOM
     */
    connectedCallback() {
      this.initialize();
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}
